import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiPhone,
  FiX,
  FiArrowRight,
  FiClock,
  FiLoader,
  FiCheck,
  FiAlertTriangle,
  FiRefreshCw,
  FiChevronLeft,
  FiShield
} from "react-icons/fi";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import { auth, waitForAuth, isAuthReady } from "./firebase";
import logo from "../logo.png";
export default function AuthModal() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(true);
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [showOtp, setShowOtp] = useState(false);
  const [error, setError] = useState("");
  const [timer, setTimer] = useState(0);
  const [loading, setLoading] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const timerRef = useRef(null);
  const recaptchaVerifierRef = useRef(null);
  const otpInputRefs = useRef([]);
  const isTestMode = process.env.NODE_ENV === 'development';

  useEffect(() => {
    const initAuth = async () => {
      try {
        await waitForAuth();
        setAuthReady(true);
      } catch (error) {
        // console.error('Failed to initialize auth:', error);
        setError('Authentication service failed to initialize. Please refresh the page.');
      }
    };

    if (isAuthReady()) {
      setAuthReady(true);
    } else {
      initAuth();
    }
  }, []);

  useEffect(() => {
    if (isTestMode && authReady) {
      setPhone("");
    }
  }, [isTestMode, authReady]);

  const cleanupRecaptcha = () => {
    if (recaptchaVerifierRef.current) {
      try {
        recaptchaVerifierRef.current.clear();
      } catch (e) {
        // console.log("Recaptcha cleanup error:", e);
      }
      recaptchaVerifierRef.current = null;
    }

    const containers = document.querySelectorAll('#recaptcha-placeholder, [id^="recaptcha-container"]');
    containers.forEach(container => {
      try {
        container.innerHTML = '';
      } catch (e) {
        // console.log("Container cleanup error:", e);
      }
    });
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      cleanupRecaptcha();
    };
  }, []);

  const startTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setTimer(30);

    timerRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const setupRecaptcha = () => {
    // Always clear existing verifier before creating a fresh instance
    cleanupRecaptcha();

    if (!auth || !auth.app) {
      throw new Error("Firebase Auth not initialized");
    }

    const containerId = 'recaptcha-placeholder';

    const verifier = new RecaptchaVerifier(
      auth,
      containerId,
      {
        'size': 'invisible',
        'callback': (response) => {
          // reCAPTCHA solved
        },
        'expired-callback': () => {
          setError("Security verification expired. Please try again.");
          cleanupRecaptcha();
        },
        'error-callback': () => {
          cleanupRecaptcha();
        }
      }
    );

    recaptchaVerifierRef.current = verifier;
    return verifier;
  };

  const sendOtp = async () => {
    if (!authReady) {
      setError("Authentication service not ready. Please wait and try again.");
      return;
    }

    setError("");
    setLoading(true);

    if (!phone || phone.length !== 10) {
      setError("Please enter a valid 10-digit phone number");
      setLoading(false);
      return;
    }

    try {
      const phoneNumber = `+91${phone}`;

      const verifier = setupRecaptcha();
      await verifier.render();

      const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, verifier);

      window.confirmationResult = confirmationResult;
      setShowOtp(true);
      startTimer();

    } catch (err) {
      cleanupRecaptcha();

      let errorMessage = "Failed to send OTP. ";

      switch (err?.code) {
        case "auth/invalid-phone-number":
          errorMessage += "Invalid phone number format.";
          break;
        case "auth/too-many-requests":
          errorMessage += "Too many attempts. Please try again later.";
          break;
        case "auth/quota-exceeded":
          errorMessage += "SMS quota exceeded. Please try again tomorrow.";
          break;
        case "auth/captcha-check-failed":
          errorMessage += "Security verification failed. Please try again.";
          break;
        case "auth/app-not-authorized":
          errorMessage += "App not authorized. Please check Firebase console settings.";
          break;
        case "auth/web-storage-unsupported":
          errorMessage += "Web storage not supported. Please enable cookies.";
          break;
        default:
          if (err?.message && err.message.includes("Cannot read properties of undefined")) {
            errorMessage += "Authentication service not ready. Please refresh and try again.";
          } else {
            errorMessage += err?.message || "Unknown error occurred.";
          }
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    const newOtp = [...otp];
    newOtp[index] = value.replace(/\D/g, '').slice(0, 1);
    setOtp(newOtp);

    // Auto-focus to next input
    if (value && index < 5) {
      otpInputRefs.current[index + 1].focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1].focus();
    }
  };

  const verifyOtp = async () => {
    setError("");
    setLoading(true);

    const otpString = otp.join('');
    if (otpString.length !== 6) {
      setError("Please enter a valid 6-digit OTP");
      setLoading(false);
      return;
    }

    try {
      if (!window.confirmationResult) {
        setError("No confirmation result found. Please request OTP again.");
        setLoading(false);
        return;
      }

      // console.log("Verifying OTP:", otpString);
      await window.confirmationResult.confirm(otpString);
      // console.log("User signed in successfully:");

      // Redirect to home page after successful login
      navigate("/");

    } catch (err) {
      // console.error("OTP verification error:", err);

      switch (err.code) {
        case "auth/invalid-verification-code":
          setError("Invalid OTP. Please check and try again.");
          break;
        case "auth/code-expired":
          setError("OTP expired. Please request a new one.");
          setShowOtp(false);
          setOtp(["", "", "", "", "", ""]);
          break;
        case "auth/session-expired":
          setError("Session expired. Please request a new OTP.");
          setShowOtp(false);
          setOtp(["", "", "", "", "", ""]);
          break;
        default:
          setError(`Verification failed: ${err.message || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const resetToPhoneInput = () => {
    setShowOtp(false);
    setOtp(["", "", "", "", "", ""]);
    setError("");
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    setTimer(0);
    cleanupRecaptcha();
  };

  const handleClose = () => {
    cleanupRecaptcha();
    setIsOpen(false);
    navigate("/");
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-[#041a0f]/85 backdrop-blur-md z-50 p-4 font-sans animate-fade-in">
      <div className="relative bg-gradient-to-b from-[#072a19] via-[#041a0f] to-[#020906] rounded-3xl shadow-[0_25px_60px_-15px_rgba(0,0,0,0.9)] p-8 sm:p-10 w-full max-w-md border border-[#eddca6]/30 text-white overflow-hidden">

        {/* Soft Ambient Light Glow Blobs inside Modal */}
        <div className="absolute -top-20 -right-20 w-44 h-44 bg-gradient-to-br from-[#eddca6]/20 to-transparent rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute -bottom-20 -left-20 w-44 h-44 bg-gradient-to-tr from-[#0b3a24]/60 to-transparent rounded-full blur-3xl pointer-events-none"></div>

        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-6 right-6 p-2 rounded-full bg-white/5 hover:bg-[#eddca6]/20 text-white/60 hover:text-[#eddca6] transition-all duration-300 border border-white/10 hover:border-[#eddca6]/30 relative z-20"
          disabled={loading}
          aria-label="Close modal"
        >
          <FiX size={18} />
        </button>

        {/* Logo & Header */}
        <div className="flex flex-col items-center text-center mb-8 relative z-10">
          <div className="mb-3 relative group">
            <div className="absolute inset-0 bg-[#eddca6]/20 rounded-full blur-md group-hover:blur-lg transition-all duration-300"></div>
            <img
              src={logo}
              alt="Chirayil Gold and Diamonds Logo"
              className="h-16 w-16 object-contain relative z-10 brightness-0 invert filter drop-shadow-[0_0_10px_rgba(237,220,166,0.4)] transition-transform duration-500 group-hover:rotate-[360deg]"
            />
          </div>

          <span className="text-xl font-serif font-black tracking-widest text-[#eddca6] uppercase">
            CHIRAYIL
          </span>
          <span className="text-[9px] uppercase tracking-[0.25em] font-medium text-white/70 -mt-0.5 mb-4">
            Gold & Diamonds
          </span>

          <h2 className="text-2xl font-serif font-bold text-white tracking-wide">
            Welcome Back
          </h2>
          <p className="text-xs text-white/75 font-light leading-relaxed mt-1 max-w-xs">
            Sign in to your gold schemes ledger using your phone number.
          </p>
        </div>

        {/* Status Messages */}
        {!authReady && (
          <div className="bg-[#0b3a24]/80 border border-[#eddca6]/30 text-[#eddca6] p-4 rounded-xl mb-6 text-xs flex items-center relative z-10 shadow-inner">
            <FiLoader className="animate-spin mr-3 flex-shrink-0 text-[#eddca6]" />
            <span className="font-medium">Initializing secure authentication service...</span>
          </div>
        )}

        {!showOtp ? (
          <div className="relative z-10">
            {/* Phone Input */}
            <div className="mb-6">
              <label className="block text-[10px] font-bold text-[#eddca6] uppercase tracking-widest mb-2.5">
                Phone Number
              </label>
              <div className="flex items-center bg-[#092e1d]/90 border border-[#eddca6]/30 rounded-xl px-4 py-3.5 focus-within:ring-2 focus-within:ring-[#eddca6]/40 focus-within:border-[#eddca6] transition-all shadow-inner hover:border-[#eddca6]/50">
                <div className="flex items-center text-[#eddca6] mr-3 border-r border-[#eddca6]/20 pr-3">
                  <FiPhone className="mr-2 text-[#eddca6] w-4 h-4" />
                  <span className="font-bold text-sm text-[#eddca6]">+91</span>
                </div>
                <input
                  type="tel"
                  placeholder="Enter 10-digit mobile number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  className="flex-1 outline-none text-white placeholder-white/40 font-semibold text-sm bg-transparent"
                  maxLength={10}
                  disabled={loading || !authReady}
                />
              </div>
              <p className="text-[10px] text-white/60 mt-2.5 flex items-center font-light">
                <FiShield className="mr-1.5 text-[#eddca6]" />
                Your credentials are encrypted & protected
              </p>
            </div>

            {error && (
              <div className="bg-red-950/70 border border-red-500/40 text-red-200 p-3.5 rounded-xl mb-5 text-xs flex items-center font-medium shadow-inner">
                <FiAlertTriangle className="mr-2.5 flex-shrink-0 w-4 h-4 text-red-400" />
                {error}
              </div>
            )}

            <button
              onClick={sendOtp}
              disabled={loading || !authReady}
              className="gold-shine-btn w-full py-4 rounded-xl bg-gradient-to-r from-[#eddca6] via-[#fff3d1] to-[#eddca6] text-[#041a0f] font-sans font-extrabold text-xs uppercase tracking-widest flex items-center justify-center transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-xl hover:scale-[1.02] active:scale-[0.98]"
            >
              {loading ? (
                <FiLoader className="animate-spin mr-2 text-[#041a0f]" />
              ) : (
                <>
                  <span>Send OTP</span>
                  <FiArrowRight className="ml-2 w-4 h-4 text-[#041a0f]" />
                </>
              )}
            </button>
          </div>
        ) : (
          <div className="relative z-10">
            {/* OTP Input */}
            <div className="mb-8">
              <div className="text-center mb-6">
                <h3 className="text-lg font-serif font-bold text-white mb-1">Verify Your Number</h3>
                <p className="text-xs text-white/70 font-light">We've sent a 6-digit verification code to</p>
                <p className="text-sm font-bold text-[#eddca6] mt-1">+91 ••••• •••{phone.slice(-2)}</p>
              </div>

              <div className="flex justify-between gap-2.5 mb-6">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    type="text"
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    ref={(el) => (otpInputRefs.current[index] = el)}
                    className="w-12 h-14 bg-[#092e1d]/90 border border-[#eddca6]/40 rounded-xl text-center text-xl font-bold text-[#eddca6] focus:ring-2 focus:ring-[#eddca6]/50 focus:border-[#eddca6] outline-none transition-all shadow-md"
                    maxLength={1}
                    disabled={loading}
                    autoFocus={index === 0}
                  />
                ))}
              </div>

              {timer > 0 ? (
                <p className="text-white/60 text-xs mb-4 flex items-center justify-center font-light">
                  <FiClock className="mr-1.5 text-[#eddca6]" /> Resend code in {timer}s
                </p>
              ) : (
                <button
                  onClick={() => {
                    resetToPhoneInput();
                    setTimeout(() => sendOtp(), 100);
                  }}
                  className="text-[#eddca6] text-xs mb-4 w-full flex items-center justify-center hover:text-[#fff3d1] transition-colors font-semibold"
                  disabled={loading}
                >
                  <FiRefreshCw className="mr-1.5 animate-spin-hover text-[#eddca6]" /> Resend OTP
                </button>
              )}

              {error && (
                <div className="bg-red-950/70 border border-red-500/40 text-red-200 p-3.5 rounded-xl mb-5 text-xs flex items-center font-medium shadow-inner">
                  <FiAlertTriangle className="mr-2.5 flex-shrink-0 w-4 h-4 text-red-400" />
                  {error}
                </div>
              )}

              <button
                onClick={verifyOtp}
                disabled={loading}
                className="gold-shine-btn w-full py-4 rounded-xl bg-gradient-to-r from-[#eddca6] via-[#fff3d1] to-[#eddca6] text-[#041a0f] font-sans font-extrabold text-xs uppercase tracking-widest flex items-center justify-center transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed mb-4 shadow-xl hover:scale-[1.02] active:scale-[0.98]"
              >
                {loading ? (
                  <FiLoader className="animate-spin mr-2 text-[#041a0f]" />
                ) : (
                  <>
                    <span>Verify OTP</span>
                    <FiCheck className="ml-2 w-4 h-4 text-[#041a0f]" />
                  </>
                )}
              </button>

              <button
                onClick={resetToPhoneInput}
                className="text-white/70 hover:text-[#eddca6] text-xs w-full flex items-center justify-center transition-colors font-medium py-2"
                disabled={loading}
              >
                <FiChevronLeft className="mr-0.5" /> Use a different number
              </button>

              <div className="mt-6 p-3.5 bg-[#092e1d]/50 rounded-xl border border-[#eddca6]/20">
                <p className="text-[10px] text-white/60 text-center leading-relaxed font-light">
                  <FiShield className="inline mr-1 text-[#eddca6]" />
                  Verification is secured with end-to-end encryption.
                </p>
              </div>
            </div>
          </div>
        )}
        <div id="recaptcha-placeholder"></div>
      </div>
    </div>
  );
}